import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Flex, Box, HStack, Text, Button, Select, Spinner, useToast, Tooltip, IconButton,
  Menu, MenuButton, MenuList, MenuItem, Badge, Input,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton,
  ModalBody, ModalFooter, FormControl, FormLabel, useDisclosure,
} from '@chakra-ui/react';
import {
  FiChevronLeft, FiPlus, FiSave, FiZap, FiGrid, FiRotateCcw, FiRotateCw, FiLock, FiUnlock,
  FiShare2, FiUsers, FiUser, FiX, FiEdit2, FiEye, FiMoreVertical,
} from 'react-icons/fi';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import ReactFlow, {
  ReactFlowProvider,
  applyNodeChanges,
  applyEdgeChanges,
  addEdge,
  MarkerType,
  Background,
  BackgroundVariant,
  Controls,
  ControlButton,
} from 'reactflow';
import 'reactflow/dist/style.css';

import Header from '../../components/Header';
import { canvasNodeTypes, rfTypeFor } from './canvasNodeTypes';
import { edgeTypes } from './OperatorEdge';
import { layoutTree, layoutRadial, layoutTiered, duplicateMetricLabels, layoutWithLanes, countNodeOverlaps } from './metricTreeLayout';
import { evalFormulaNodes, findFormulaCycle } from './formulaEval';
import MetricNodePanel from './MetricNodePanel';
import NodePalette from './NodePalette';
import TemplateChooser from './TemplateChooser';
import { templateByKey } from './metricTreeTemplates';
import { metricTreeApi, TIME_FRAMES, newNodeData } from './metricTreeApi';
import API from '../../helpers/axiosInstance';
import { getApiHeaders, hasPermission } from '../../utils/DefaultFunction';

// Module-level so React Flow doesn't warn about a new object each render.
const NODE_TYPES = canvasNodeTypes;
const EDGE_TYPES = edgeTypes;

// Colour an edge by correlation strength |r|.
const corrColor = (r) => {
  const a = Math.abs(Number(r) || 0);
  if (a >= 0.7) return '#38A169'; // strong — green
  if (a >= 0.4) return '#DD6B20'; // moderate — orange
  return '#A0AEC0'; // weak — gray
};

let uidCounter = 0;
const uid = (prefix) => `${prefix}_${Date.now().toString(36)}_${(uidCounter += 1)}`;

// Project a formula node's inputs into operator edges (source=formula, target=input).
// Used both for the live canvas edges and to feed the auto-layout.
function deriveFormulaEdges(rfNodes) {
  const ids = new Set(rfNodes.map((n) => n.id));
  const out = [];
  rfNodes.forEach((n) => {
    if (n.data?.kind === 'formula') {
      const op = n.data?.formula?.operator || '+';
      (n.data?.formula?.inputs || []).forEach((inId) => {
        if (ids.has(inId)) {
          out.push({
            id: `f_${n.id}__${inId}`, source: n.id, target: inId,
            type: 'operator', data: { kind: 'formula', operator: op },
            markerEnd: { type: MarkerType.ArrowClosed, color: '#9DB0C6' },
          });
        }
      });
    }
  });
  return out;
}

function CanvasInner() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const toast = useToast();

  const [featurePermissions, setFeaturePermissions] = useState([]);
  const [featurePermissionsLoading, setFeaturePermissionsLoading] = useState(true);
  // Backend groups metric-tree permissions under the list page's own feature
  // slug, not this canvas's route (which includes the tree id).
  const featurePermParams = "analytics/metric-tree";

  const fetchFeaturePermissions = async () => {
    setFeaturePermissionsLoading(true);
    try {
      const url = `${process.env.REACT_APP_API_URL}/permission/feature/list?slug=${featurePermParams}`;
      const response = await API.get(url, { headers: getApiHeaders() });
      setFeaturePermissions(response.data.data || []);
    } catch (error) {
      console.error("Error fetching permissions:", error);
      setFeaturePermissions([]);
    } finally {
      setFeaturePermissionsLoading(false);
    }
  };
  useEffect(() => {
    fetchFeaturePermissions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const metricTreeViewPermission = hasPermission("metric-tree-view", featurePermissions);
  const metricTreeSharePermission = hasPermission("metric-tree-share", featurePermissions);
  const metricTreeEditPermission = hasPermission("metric-tree-edit", featurePermissions);
  const metricTreeCreatePermission = hasPermission("metric-tree-create", featurePermissions);

  const isNew = !id || id === 'new';
  // PERM-190: the metric-tree route no longer accepts metric-tree-create on its
  // own (a create-only user could otherwise open ANY existing tree by URL) —
  // only this specific tree, reached right after its own creation, gets in via
  // PermissionRoute's allowIfState (location.state.justCreated + metric-tree-create).
  // canEdit below treats that the same as isNew so the creator can still save it.
  const justCreated = Boolean(location.state?.justCreated);
  // BUG-MT-18: a tree reached straight from the creation flow opens in EDIT (an
  // empty/just-created tree has nothing to preview). The list passes { edit:true }
  // in the navigation state after create.
  const openInEdit = isNew || location.state?.edit === true;

  const [treeName, setTreeName] = useState('New Metric Tree');
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [timeFrame, setTimeFrame] = useState('all_time');
  // 'horizontal' (left→right formula tree) or 'vertical' (top-down KPI tree —
  // Mixpanel style: strategic root on top, hierarchy connectors flowing up).
  const [orientation, setOrientation] = useState('horizontal');

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [computing, setComputing] = useState(false);

  // ── MT-01: ownership + sharing (mirrors Boards) ──
  // owner_id / scope / editors come from the loaded tree; a NEW tree is owned by
  // the current user (personal by default) until the first save.
  const [scope, setScope] = useState('personal');
  const [ownerId, setOwnerId] = useState(null);
  const [editorUserIds, setEditorUserIds] = useState([]);
  // MT-02: existing trees open in read-only VIEW; a new tree (or one opened from
  // the creation flow, MT-18) starts in EDIT.
  const [mode, setMode] = useState(openInEdit ? 'edit' : 'view');

  const currentUserId = useMemo(() => {
    try { const u = JSON.parse(localStorage.getItem('user') || 'null'); return String(u?.id || u?.username || ''); }
    catch (_) { return ''; }
  }, []);
  // A new (unsaved) tree is always yours. An editor can edit content; only the
  // owner can rename/reshare/move scope (enforced server-side too). BUG-MT-21: a
  // TEAM tree is visible AND editable team-wide, so any tenant member may edit its
  // content (the server enforces the same — see canEdit in metricTree.controller).
  const isOwner = isNew || (currentUserId !== '' && ownerId != null && String(ownerId) === currentUserId);
  const isEditor = currentUserId !== '' && editorUserIds.map(String).includes(currentUserId);
  const canEdit = (
    (isNew ? metricTreeCreatePermission : metricTreeEditPermission) ||
    (!isNew && justCreated && metricTreeCreatePermission)
  ) && (isNew || isOwner || isEditor || scope === 'team');
  const editing = mode === 'edit' && canEdit;

  // Share dialog + tenant directory (identical contract to Boards).
  const shareModal = useDisclosure();
  const [userDir, setUserDir] = useState([]);
  const [userDirState, setUserDirState] = useState('idle');
  const [shareSelected, setShareSelected] = useState([]);
  const [userQuery, setUserQuery] = useState('');
  const [scopeBusy, setScopeBusy] = useState(false);

  // Canvas tool (pan/select) + the "where to start" template chooser (new trees).
  const [tool, setTool] = useState('select');
  // MT-05: "lock layout" fixes node POSITIONS only — metric editing stays on.
  const [layoutLocked, setLayoutLocked] = useState(false);
  const [showChooser, setShowChooser] = useState(isNew);

  const [events, setEvents] = useState([]);
  const [eventsError, setEventsError] = useState(null);
  const [eventsLoading, setEventsLoading] = useState(true);

  // Track the persisted id after first create so re-saves reuse the record.
  const savedIdRef = useRef(isNew ? null : id);

  // ── Unsaved-changes + undo/redo (BUG-MT-01/02/03) ──
  // `dirty` = there are edits not yet saved. Drives the Refresh guard, the
  // beforeunload prompt, and the Save affordance.
  const [dirty, setDirty] = useState(false);
  const past = useRef([]); // [{nodes,edges,treeName,orientation}] — undo stack
  const future = useRef([]); // redo stack
  const lastPushAt = useRef(0);
  const [, setHistTick] = useState(0); // re-render undo/redo button enabled state

  // Snapshot the CURRENT state as a restore point and mark the tree dirty. Called
  // at the START of every user edit (before the mutation). Rapid edits (typing) are
  // coalesced into one history entry unless opts.force.
  const pushHistory = useCallback((opts = {}) => {
    // BUG-MT-10: opts.markDirty === false snapshots for undo WITHOUT flagging the
    // tree dirty (used at drag START, which also fires on a plain click — a
    // zero-distance drag — where nothing has actually changed yet).
    if (opts.markDirty !== false) setDirty(true);
    const now = Date.now();
    if (!opts.force && now - lastPushAt.current < 450) { lastPushAt.current = now; return; }
    lastPushAt.current = now;
    past.current.push({ nodes, edges, treeName, orientation });
    if (past.current.length > 60) past.current.shift();
    future.current = [];
    setHistTick((t) => t + 1);
  }, [nodes, edges, treeName, orientation]);

  const restore = useCallback((snap) => {
    setNodes(snap.nodes);
    setEdges(snap.edges);
    setTreeName(snap.treeName);
    setOrientation(snap.orientation);
    setDirty(true);
  }, []);
  const undo = useCallback(() => {
    if (!past.current.length) return;
    future.current.push({ nodes, edges, treeName, orientation });
    restore(past.current.pop());
    setHistTick((t) => t + 1);
  }, [nodes, edges, treeName, orientation, restore]);
  const redo = useCallback(() => {
    if (!future.current.length) return;
    past.current.push({ nodes, edges, treeName, orientation });
    restore(future.current.pop());
    setHistTick((t) => t + 1);
  }, [nodes, edges, treeName, orientation, restore]);

  // Warn before losing unsaved edits on reload / navigate-away (BUG-MT-02).
  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  // Ctrl/Cmd+Z = undo, Ctrl/Cmd+Shift+Z or Ctrl+Y = redo (BUG-MT-03). Ignored
  // while typing in a field so native text undo still works there.
  useEffect(() => {
    const onKey = (e) => {
      if (mode !== 'edit') return; // MT-02: no history mutation in view mode
      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;
      const el = document.activeElement;
      const tag = (el && el.tagName ? el.tagName : '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || (el && el.isContentEditable)) return;
      const k = (e.key || '').toLowerCase();
      if (k === 'z') { e.preventDefault(); if (e.shiftKey) redo(); else undo(); }
      else if (k === 'y') { e.preventDefault(); redo(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo, mode]);

  // ── Load event list once (for node config dropdowns) ──
  useEffect(() => {
    let alive = true;
    setEventsLoading(true);
    metricTreeApi
      .listEvents()
      .then((r) => {
        if (!alive) return;
        setEvents(r.data || []);
        setEventsError(r.error || null);
      })
      .finally(() => alive && setEventsLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  // ── Load the tree when editing ──
  useEffect(() => {
    let alive = true;
    if (isNew) return undefined;
    setLoading(true);
    metricTreeApi
      .get(id)
      .then((r) => {
        if (!alive) return;
        const tree = r?.data || {};
        setTreeName(tree.name || 'Metric Tree');
        setTimeFrame(tree.default_time_frame || 'all_time');
        setOrientation(tree.layout?.orientation === 'vertical' ? 'vertical' : 'horizontal');
        // MT-01: capture ownership/scope so the toolbar can gate owner actions.
        setScope(tree.scope === 'team' ? 'team' : 'personal');
        setOwnerId(tree.owner_id != null ? String(tree.owner_id) : null);
        setEditorUserIds(Array.isArray(tree.editor_user_ids) ? tree.editor_user_ids.map(String) : []);
        // MT-02: land in read-only view; the user clicks Edit to make changes.
        // BUG-MT-18: unless the tree was reached from the creation flow (openInEdit),
        // in which case it stays in edit — the load must NOT clobber it back to view.
        setMode(openInEdit ? 'edit' : 'view');
        setNodes((tree.nodes || []).map(toRfNode));
        // Formula edges are re-derived from formula nodes' inputs; only load the
        // drawn correlation edges into state.
        setEdges((tree.edges || [])
          .filter((e) => (e.kind || 'correlation') !== 'formula')
          .map(toRfEdge));
        savedIdRef.current = tree._id || id;
        // Fresh load = clean slate: no unsaved edits, empty undo history.
        setDirty(false);
        past.current = [];
        future.current = [];
      })
      .catch((e) => {
        if (!alive) return;
        // MT-19: an expired/rejected session must send the user to login, not leave
        // them on a broken empty canvas. The login page is mounted at "/" (App.js).
        const status = e?.response?.status;
        if (status === 401 || status === 403) {
          try { localStorage.removeItem('customer-portal'); } catch (_) { /* ignore */ }
          navigate('/');
          return;
        }
        toast({
          title: 'Failed to load metric tree',
          description: e?.response?.data?.message || e.message,
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [id, isNew, toast, openInEdit, navigate]);

  const onNodesChange = useCallback(
    (changes) => {
      // BUG-MT-10: selection ('select'), measurement ('dimensions') and in-flight
      // drag ('position' with dragging:true/undefined) are READ actions — they must
      // never dirty the tree. Critically, a committed move is NOT handled here
      // either: React Flow emits a `position` change with dragging:false even for a
      // zero-distance drag (a plain click), so keying dirty off that flag falsely
      // dirtied the tree on selection. A real move is detected in onNodeDragStop
      // (which compares start vs end position). Only a delete marks dirty here.
      if (changes.some((c) => c.type === 'remove')) pushHistory({ force: true });
      setNodes((nds) => applyNodeChanges(changes, nds));
    },
    [pushHistory],
  );
  // Only correlation edges live in state; formula edges are derived from nodes
  // (below) and re-projected each render, so ignore changes targeting them.
  // BUG-MT-10: edge selection is a read action too — only a remove dirties here
  // (a new connection dirties via onConnect).
  const onEdgesChange = useCallback(
    (changes) => {
      const real = changes.filter((c) => !String(c.id || '').startsWith('f_'));
      if (real.some((c) => c.type === 'remove')) pushHistory({ force: true });
      setEdges((eds) => applyEdgeChanges(real, eds));
    },
    [pushHistory],
  );

  // BUG-MT-10: drag lifecycle. onNodeDragStart snapshots the pre-drag state for
  // undo but does NOT mark dirty (a click registers as a zero-distance drag).
  // onNodeDragStop commits that snapshot + the dirty flag ONLY if the node moved.
  const dragMeta = useRef(null);
  const onNodeDragStart = useCallback((_e, node) => {
    dragMeta.current = {
      startPos: node && node.position ? { x: node.position.x, y: node.position.y } : null,
      snap: { nodes, edges, treeName, orientation },
    };
  }, [nodes, edges, treeName, orientation]);
  const onNodeDragStop = useCallback((_e, node) => {
    const meta = dragMeta.current; dragMeta.current = null;
    if (!meta || !meta.startPos || !node || !node.position) return;
    const moved = Math.abs(meta.startPos.x - node.position.x) > 0.5
                || Math.abs(meta.startPos.y - node.position.y) > 0.5;
    if (!moved) return; // a click, not a drag → not an edit
    past.current.push(meta.snap);
    if (past.current.length > 60) past.current.shift();
    future.current = [];
    setHistTick((t) => t + 1);
    setDirty(true);
  }, []);

  const onConnect = useCallback((params) => {
    pushHistory({ force: true });
    const newEdge = {
      ...params,
      id: uid('edge'),
      type: 'default',
      animated: false,
      markerEnd: { type: MarkerType.ArrowClosed },
      style: { strokeWidth: 2, stroke: '#A0AEC0' },
      data: { kind: 'correlation' },
    };
    setEdges((eds) => addEdge(newEdge, eds));
  }, [pushHistory]);

  const handleAddNode = useCallback((type = 'metric') => {
    pushHistory({ force: true });
    const nodeId = uid('node');
    setNodes((nds) => {
      // MT-13: cascade each insertion by a fixed offset (wrapped every 8) so a
      // new node never lands exactly on top of the previous one and is readable
      // immediately, instead of all insertions stacking on one coordinate.
      const step = nds.length % 8;
      const newNode = {
        id: nodeId,
        type: rfTypeFor(type),
        position: { x: 180 + step * 44, y: 130 + step * 44 },
        data: newNodeData(type),
      };
      return [...nds, newNode];
    });
    setSelectedNodeId(nodeId);
    setShowChooser(false);
  }, [pushHistory]);

  // Seed a starter tree from a template (Mixpanel "Build" flow). opts.ai is
  // reserved for future LLM generation; it seeds the same starter for now.
  const applyTemplate = useCallback((key /* , opts */) => {
    const t = templateByKey(key);
    if (!t) return;
    pushHistory({ force: true });
    const def = t.build();
    setTreeName(def.name || 'Metric Tree');
    setOrientation(t.orientation === 'vertical' ? 'vertical' : 'horizontal');
    if (def.default_time_frame) setTimeFrame(def.default_time_frame);
    const rfEdges = (def.edges || [])
      .filter((e) => (e.kind || 'correlation') !== 'formula')
      .map(toRfEdge);
    let rfNodes = (def.nodes || []).map(toRfNode);
    // Radial templates get auto-arranged; fixed templates (KPI scaffold) keep
    // their explicit top-down positions.
    if (t.layout === 'radial') rfNodes = layoutRadial(rfNodes, rfEdges);
    setNodes(rfNodes);
    setEdges(rfEdges);
    setSelectedNodeId(null);
    setShowChooser(false);
  }, [pushHistory]);

  const handleNodeClick = useCallback((_e, node) => setSelectedNodeId(node.id), []);
  const handlePaneClick = useCallback(() => setSelectedNodeId(null), []);

  const patchSelectedNode = useCallback(
    (patch) => {
      pushHistory();
      setNodes((nds) =>
        nds.map((n) => (n.id === selectedNodeId ? { ...n, data: { ...n.data, ...patch } } : n)),
      );
    },
    [selectedNodeId, pushHistory],
  );

  const deleteSelectedNode = useCallback(() => {
    pushHistory({ force: true });
    setNodes((nds) => nds.filter((n) => n.id !== selectedNodeId));
    setEdges((eds) => eds.filter((e) => e.source !== selectedNodeId && e.target !== selectedNodeId));
    setSelectedNodeId(null);
  }, [selectedNodeId, pushHistory]);

  // ── Save ──
  const handleSave = useCallback(async () => {
    // Validate: metric nodes using sum/avg must have a property.
    const invalid = nodes.find(
      (n) => n.data?.kind !== 'formula'
        && n.data?.metric?.event // only enforce once an event is bound
        && ['sum', 'avg'].includes(n.data?.metric?.agg) && !n.data?.metric?.property,
    );
    if (invalid) {
      toast({
        title: 'Missing property',
        description: `"${invalid.data?.label || 'A node'}" uses Sum/Average and needs a numeric property.`,
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    // Validate: formula nodes must have at least one input.
    const emptyFormula = nodes.find(
      (n) => n.data?.kind === 'formula' && !(n.data?.formula?.inputs || []).length,
    );
    if (emptyFormula) {
      toast({
        title: 'Formula needs inputs',
        description: `"${emptyFormula.data?.label || 'A formula node'}" has no inputs selected.`,
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    // BUG-MT-05: reject a formula graph with a circular reference rather than let
    // the cycle silently evaluate to 0.
    const cycle = findFormulaCycle(nodes);
    if (cycle) {
      toast({
        title: 'Circular reference',
        description: `"${cycle.label}" is part of a formula loop — a node can't reference itself directly or through its inputs. Remove the looping input before saving.`,
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
      return;
    }

    // BUG-MT-20: duplicate labels make formula inputs ambiguous and the saved tree
    // unreadable. This is a BLOCKING validation error — the save must NOT proceed
    // while two formula-eligible metrics share a label. List the colliding labels
    // and return so nothing is persisted and no "saved" toast is shown.
    const dupLabels = duplicateMetricLabels(nodes);
    if (dupLabels.length) {
      toast({
        title: 'Duplicate node labels',
        description: `${dupLabels.map((l) => `"${l}"`).join(', ')} ${dupLabels.length === 1 ? 'is used by more than one metric' : 'are each used by more than one metric'}. Every metric label must be unique — rename the duplicates before saving.`,
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
      return; // block the save; the duplicate metric must not be persisted
    }

    setSaving(true);
    try {
      // Derive formula edges straight from the formula nodes (kept in sync with
      // the canvas projection) so persistence depends only on nodes + edges.
      const nodeIdSet = new Set(nodes.map((n) => n.id));
      const derivedFormulaEdges = [];
      nodes.forEach((n) => {
        if (n.data?.kind === 'formula') {
          const op = n.data?.formula?.operator || '+';
          (n.data?.formula?.inputs || []).forEach((inId) => {
            if (nodeIdSet.has(inId)) {
              derivedFormulaEdges.push({
                edge_id: `f_${n.id}__${inId}`, source: n.id, target: inId,
                kind: 'formula', operator: op,
              });
            }
          });
        }
      });

      const payload = {
        name: treeName?.trim() || 'Untitled Metric Tree',
        default_time_frame: timeFrame,
        nodes: nodes.map(fromRfNode),
        // Persist drawn correlation edges + the derived formula edges (so the tree
        // reloads with its operator badges without re-deriving from scratch).
        edges: [...edges.map(fromRfEdge), ...derivedFormulaEdges],
        layout: { orientation },
      };
      let res;
      if (savedIdRef.current) {
        res = await metricTreeApi.update(savedIdRef.current, payload);
      } else {
        res = await metricTreeApi.create(payload);
        const newId = res?.data?._id;
        if (newId) {
          savedIdRef.current = newId;
          // BUG-MT-18: keep the just-saved new tree in EDIT (the user is mid-build);
          // carry the flag so the id-swap re-mount doesn't drop them to view.
          navigate(`/analytics/metric-tree/${newId}`, { replace: true, state: { edit: true } });
        }
      }
      toast({ title: 'Metric tree saved', status: 'success', duration: 2500, isClosable: true });
      setDirty(false); // saved — clear the unsaved-changes state
    } catch (e) {
      toast({
        title: 'Save failed',
        description: e?.response?.data?.message || e.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  }, [nodes, edges, treeName, timeFrame, orientation, navigate, toast]);

  // ── Compute (silent:true skips the toasts — used for auto-load on open) ──
  // Merge a compute/preview result (node values + edge correlations) into state.
  // Keyed by node_id / edge_id; leaves un-returned nodes/edges untouched.
  const applyComputeResult = useCallback((data) => {
    const byNode = new Map((data.nodes || []).map((n) => [n.node_id, n]));
    const corrByEdge = new Map((data.edges || []).map((e) => [e.edge_id, e]));
    setNodes((nds) =>
      nds.map((n) => {
        const c = byNode.get(n.id);
        if (!c) return n;
        return {
          ...n,
          data: {
            ...n.data,
            value: c.value, series: c.series || [], delta_pct: c.delta_pct,
            wow_pct: c.wow_pct, qoq_pct: c.qoq_pct,
          },
        };
      }),
    );
    setEdges((eds) =>
      eds.map((e) => {
        const c = corrByEdge.get(e.id);
        if (!c || c.corr === null || c.corr === undefined) return e;
        const r = Number(c.corr);
        return {
          ...e,
          label: `r=${r.toFixed(2)}`,
          labelBgPadding: [6, 3],
          labelBgBorderRadius: 6,
          labelBgStyle: { fill: '#fff', fillOpacity: 0.9 },
          labelStyle: { fill: corrColor(r), fontWeight: 700, fontSize: 12 },
          style: { strokeWidth: 2 + Math.abs(r) * 2, stroke: corrColor(r) },
          data: { ...e.data, corr: { value: r, n: c.n } },
        };
      }),
    );
  }, []);

  const handleCompute = useCallback(async (opts = {}) => {
    const silent = opts === true || opts?.silent === true;
    // BUG-MT-01: Refresh recomputes server-side from the SAVED tree. With unsaved
    // edits (e.g. a changed aggregation) the returned value would render under the
    // new label — a stale, misleading mismatch. Block manual refresh until saved.
    if (!silent && dirty) {
      toast({
        title: 'Unsaved changes',
        description: 'Save the tree before refreshing — otherwise values reflect the last saved version, not your latest edits.',
        status: 'warning', duration: 5000, isClosable: true,
      });
      return;
    }
    if (!savedIdRef.current) {
      if (!silent) {
        toast({
          title: 'Save first',
          description: 'Save the tree before computing its metrics.',
          status: 'info',
          duration: 3500,
          isClosable: true,
        });
      }
      return;
    }
    setComputing(true);
    try {
      const res = await metricTreeApi.compute(savedIdRef.current, { time_frame: timeFrame });
      const data = res?.data || {};
      applyComputeResult(data);

      if (!silent) {
        toast({
          title: 'Computed',
          description: `${data.cache_hit ? 'From cache · ' : ''}${data.time_frame || timeFrame}`,
          status: 'success',
          duration: 2500,
          isClosable: true,
        });
      }
    } catch (e) {
      toast({
        title: 'Compute failed',
        description: e?.response?.data?.message || e.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setComputing(false);
    }
  }, [timeFrame, toast, dirty, applyComputeResult]);

  // Auto-load values: compute (silently) once a saved tree has loaded, and again
  // whenever the time frame changes — no Compute click required.
  useEffect(() => {
    if (loading || !savedIdRef.current) return;
    handleCompute({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, timeFrame]);

  // MT-08: compute-relevant signature — only the fields that change a value
  // (event/agg/property/events/formula per node + edges + time frame). Excludes
  // value/series/label/owner/position, so merging a result never re-triggers.
  const computeSignature = useMemo(() => {
    const ns = nodes.map((n) => ({
      id: n.id, kind: n.data?.kind, type: n.data?.type,
      metric: n.data?.metric, formula: n.data?.formula,
    }));
    const es = edges.map((e) => ({ s: e.source, t: e.target, k: e.data?.kind || e.kind }));
    return JSON.stringify({ ns, es, timeFrame });
  }, [nodes, edges, timeFrame]);

  // MT-08: a node configured in the builder should show a value at runtime, not
  // only after save + reload. Debounced preview computes the IN-MEMORY graph
  // (POST /metric-trees/preview) whenever the compute signature changes, so an
  // unsaved metric node renders its value/sparkline immediately. Server metrics
  // only; formula nodes already evaluate client-side.
  useEffect(() => {
    if (loading) return undefined;
    const hasMetric = nodes.some((n) => n.data?.kind !== 'formula'
      && (n.data?.metric?.event || (Array.isArray(n.data?.metric?.events) && n.data?.metric?.events.length > 0)));
    if (!hasMetric) return undefined;
    const t = setTimeout(async () => {
      try {
        const nodeIdSet = new Set(nodes.map((n) => n.id));
        const derived = [];
        nodes.forEach((n) => {
          if (n.data?.kind === 'formula') {
            const op = n.data?.formula?.operator || '+';
            (n.data?.formula?.inputs || []).forEach((inId) => {
              if (nodeIdSet.has(inId)) derived.push({ edge_id: `f_${n.id}__${inId}`, source: n.id, target: inId, kind: 'formula', operator: op });
            });
          }
        });
        const res = await metricTreeApi.preview({
          nodes: nodes.map(fromRfNode),
          edges: [...edges.map(fromRfEdge), ...derived],
          time_frame: timeFrame,
        });
        applyComputeResult(res?.data || {});
      } catch (_) { /* preview is best-effort — Compute/Save still work */ }
    }, 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [computeSignature, loading]);

  // ── Derived formula edges + root marking (a formula node no other formula
  // references is the north-star). Formula edges are a projection of each formula
  // node's inputs, so the canvas draws them automatically — the user configures
  // inputs in the panel, not by hand-drawing edges. ──
  // Horizontal trees draw operator-badge edges derived from formula inputs.
  // Vertical (KPI) trees use the stored top-down hierarchy connectors instead
  // (Mixpanel shows plain connectors, not operators), so suppress the derived ones.
  const formulaEdges = useMemo(
    () => (orientation === 'vertical' ? [] : deriveFormulaEdges(nodes)),
    [nodes, orientation],
  );

  const rootIds = useMemo(() => {
    const referenced = new Set();
    nodes.forEach((n) => (n.data?.formula?.inputs || []).forEach((id) => referenced.add(id)));
    const ids = new Set();
    nodes.forEach((n) => {
      if (n.data?.kind === 'formula' && !referenced.has(n.id)) ids.add(n.id);
    });
    return ids;
  }, [nodes]);

  // Pin toggles a node flag; comment selects the node so its panel (with the
  // comment thread) opens. Both are injected into node.data for the card footer.
  const togglePin = useCallback((id) => {
    pushHistory({ force: true });
    setNodes((nds) => nds.map((n) => (n.id === id
      ? { ...n, data: { ...n.data, pinned: !n.data?.pinned } } : n)));
  }, [pushHistory]);
  const openComments = useCallback((id) => setSelectedNodeId(id), []);

  // Formula nodes are re-derived from their inputs client-side, so the card updates
  // the instant you change an operator or reorder inputs (no Save/recompute needed).
  const formulaComputed = useMemo(() => evalFormulaNodes(nodes, timeFrame), [nodes, timeFrame]);

  const displayNodes = useMemo(
    () => nodes.map((n) => ({
      ...n,
      data: {
        ...n.data,
        ...(rootIds.has(n.id) ? { isRoot: true } : {}),
        ...(formulaComputed[n.id] || {}),
        computing,
        onTogglePin: togglePin,
        onOpenComments: openComments,
      },
    })),
    [nodes, rootIds, formulaComputed, computing, togglePin, openComments],
  );
  const allEdges = useMemo(() => [...formulaEdges, ...edges], [formulaEdges, edges]);

  // MT-11: the structured layout for the current tree. Vertical KPI trees tier
  // top-down; a hierarchy of drawn connectors tiers left-to-right; a formula graph
  // uses the tree layout; a loose graph falls back to radial. Lane/section header
  // nodes (presentational, edge-less) are partitioned OUT of the edge-driven pass
  // by layoutWithLanes and re-placed against the lane they label, so Tidy never
  // strands a header or packs it into the flow. Deterministic → idempotent.
  const computeTidyLayout = useCallback((nds) => {
    const base = (subset) => {
      if (orientation === 'vertical') return layoutTiered(subset, edges, { orientation: 'vertical', parentIsTarget: true });
      if (formulaEdges.length) return layoutTree(subset, formulaEdges);
      if (edges.length) return layoutTiered(subset, edges, { orientation: 'horizontal', parentIsTarget: true });
      return layoutRadial(subset, edges);
    };
    return layoutWithLanes(nds, base, orientation);
  }, [orientation, formulaEdges, edges]);

  const handleTidy = useCallback(() => {
    // MT-11: Tidy RE-APPLIES the structured layout. It applies whenever the layout
    // would MOVE any node — e.g. after nodes were dragged out of their lanes — not
    // only when nodes overlap (the earlier no-overlap early-return made Tidy a
    // no-op on template trees whose dragged-out nodes didn't happen to overlap).
    // A tree already matching its layout is a natural no-op → "Already tidy", so a
    // clean template is never degraded.
    const next = computeTidyLayout(nodes);

    // MT-11 guard: Tidy must never make a layout worse. If the result would have
    // MORE overlapping cards than the input, discard it and leave the canvas as-is
    // (turns a data-destroying layout into a safe no-op in the worst case).
    if (countNodeOverlaps(next) > countNodeOverlaps(nodes)) {
      toast({ title: 'Tidy skipped', description: 'Auto-layout would overlap cards on this tree — left the canvas unchanged.', status: 'warning', duration: 3000, isClosable: true });
      return;
    }

    const byId = new Map(nodes.map((n) => [n.id, n]));
    const changed = next.some((n) => {
      const cur = byId.get(n.id);
      return !cur
        || Math.abs((cur.position?.x ?? 0) - (n.position?.x ?? 0)) > 0.5
        || Math.abs((cur.position?.y ?? 0) - (n.position?.y ?? 0)) > 0.5;
    });
    if (!changed) {
      toast({ title: 'Already tidy', description: 'Nodes are already in a clean layout.', status: 'info', duration: 2000, isClosable: true });
      return;
    }
    pushHistory({ force: true }); // MT-11: Tidy is one atomic, undoable step.
    setNodes(next);
  }, [computeTidyLayout, pushHistory, nodes, toast]);

  // ── MT-02: enter / leave edit mode ──
  const enterEdit = useCallback(() => setMode('edit'), []);
  const exitToView = useCallback(() => {
    if (dirty) {
      toast({
        title: 'Unsaved changes',
        description: 'Save the tree before switching back to view mode.',
        status: 'warning', duration: 4000, isClosable: true,
      });
      return;
    }
    setSelectedNodeId(null);
    setMode('view');
  }, [dirty, toast]);

  // ── MT-01: share dialog (owner + personal scope only, mirrors Boards) ──
  const openShare = useCallback(() => {
    if (!savedIdRef.current) {
      toast({ title: 'Save first', description: 'Save the tree before sharing it.', status: 'info', duration: 3000, isClosable: true });
      return;
    }
    setUserQuery('');
    const ids = editorUserIds.map((x) => String(x));
    setShareSelected(ids.map((uid2) => ({ id: uid2, name: `User ${uid2}`, email: '' })));
    shareModal.onOpen();
    setUserDirState('loading');
    metricTreeApi.listUsers()
      .then((r) => {
        const dir = r.data || [];
        setUserDir(dir);
        setUserDirState(r.error ? 'error' : 'ready');
        setShareSelected((prev) => prev.map((s) => {
          const u = dir.find((d) => d.id === s.id);
          return u ? { id: u.id, name: u.name, email: u.email } : s;
        }));
      })
      .catch(() => setUserDirState('error'));
  }, [editorUserIds, shareModal, toast]);

  const addShareUser = useCallback((u) => {
    setShareSelected((p) => (p.some((s) => s.id === u.id) ? p : [...p, u]));
    setUserQuery('');
  }, []);
  const removeShareUser = useCallback((rid) => setShareSelected((p) => p.filter((s) => s.id !== rid)), []);

  const saveShare = useCallback(async () => {
    const ids = shareSelected.map((s) => s.id);
    setScopeBusy(true);
    try {
      await metricTreeApi.update(savedIdRef.current, { editor_user_ids: ids });
      setEditorUserIds(ids);
      toast({ status: 'success', title: ids.length ? 'Tree shared' : 'Sharing cleared', duration: 2000 });
      shareModal.onClose();
    } catch (e) {
      const code = e?.response?.data?.error?.code;
      toast({
        status: 'error', title: 'Share failed',
        description: code === 'unknown_editors'
          ? 'Some selected users could not be validated — please re-pick them from the list.'
          : (e?.response?.data?.error?.message || e.message),
        duration: 5000, isClosable: true,
      });
    } finally {
      setScopeBusy(false);
    }
  }, [shareSelected, shareModal, toast]);

  // MT-01: owner moves a tree between Personal and Team scope.
  const moveScope = useCallback(async (next) => {
    if (!savedIdRef.current) {
      toast({ title: 'Save first', description: 'Save the tree before changing its scope.', status: 'info', duration: 3000, isClosable: true });
      return;
    }
    setScopeBusy(true);
    try {
      await metricTreeApi.update(savedIdRef.current, { scope: next });
      setScope(next);
      toast({ status: 'success', title: next === 'team' ? 'Moved to Team' : 'Moved to Personal', duration: 2000 });
    } catch (e) {
      toast({ status: 'error', title: 'Scope change failed', description: e?.response?.data?.error?.message || e.message, duration: 4000, isClosable: true });
    } finally {
      setScopeBusy(false);
    }
  }, [toast]);

  const shareSelectedIds = new Set(shareSelected.map((s) => s.id));
  const shareCandidates = userDir
    .filter((u) => !shareSelectedIds.has(u.id))
    .filter((u) => {
      const q = userQuery.trim().toLowerCase();
      if (!q) return true;
      return u.name.toLowerCase().includes(q)
        || (u.email || '').toLowerCase().includes(q)
        || (u.username || '').toLowerCase().includes(q)
        || u.id.includes(q);
    })
    .slice(0, 25);

  const selectedNode = nodes.find((n) => n.id === selectedNodeId) || null;

  if (featurePermissionsLoading) {
    return (
      <Box h="100vh" display="flex" flexDirection="column">
        <Header header="Metric Tree" />
        <Flex align="center" justify="center" flex="1">
          <Spinner size="lg" color="blue.500" />
        </Flex>
      </Box>
    );
  }

  return (
    <Box h="100vh" display="flex" flexDirection="column">
      <Header header="Metric Tree" />
      <Flex direction="column" flex="1" minH={0}>
        {/* Toolbar */}
        <Flex
          align="center"
          justify="space-between"
          px={4}
          py={2}
          borderBottom="1px"
          borderColor="gray.200"
          bg="white"
        >
          <HStack spacing={3}>
            <Tooltip label="Back to list">
              <IconButton
                size="sm" variant="ghost" icon={<FiChevronLeft />}
                aria-label="Back" onClick={() => navigate('/analytics/metric-tree')}
              />
            </Tooltip>
            <input
              value={treeName}
              onChange={(e) => { pushHistory(); setTreeName(e.target.value); }}
              // MT-01: only the owner renames (backend enforces the same); view
              // mode / editors get a read-only title.
              readOnly={!(editing && isOwner)}
              title={!isOwner ? 'Only the owner can rename this tree' : undefined}
              style={{
                fontSize: 16, fontWeight: 700, border: 'none', outline: 'none',
                background: 'transparent', minWidth: 200,
                cursor: editing && isOwner ? 'text' : 'default',
              }}
            />
            {/* MT-01: scope badge — Personal vs Team, at a glance. */}
            <Badge
              colorScheme={scope === 'team' ? 'purple' : 'gray'}
              variant="subtle" textTransform="capitalize" borderRadius="full" px={2}
            >
              {scope}
            </Badge>
            {/* MT-01: shared indicator (owner can click to manage). */}
            {editorUserIds.length > 0 && (
              <Tooltip label={isOwner && metricTreeSharePermission ? 'Manage who has access' : 'Shared with you'}>
                <HStack
                  as={isOwner && metricTreeSharePermission ? 'button' : 'div'}
                  onClick={isOwner && metricTreeSharePermission ? openShare : undefined}
                  spacing={1} bg="#e0e7ff" color="#3730a3" borderRadius="full" px={2} py={0.5}
                  cursor={isOwner && metricTreeSharePermission ? 'pointer' : 'default'}
                >
                  <FiUsers size={11} />
                  <Text fontSize="xs" fontWeight={700}>Shared · {editorUserIds.length}</Text>
                </HStack>
              </Tooltip>
            )}
          </HStack>

          <HStack spacing={3}>
            {/* MT-02: editing tools appear only in edit mode. Also require metric-tree-view
                (the only permission slug this feature has — see PERMISSION_SLUGS.md), since
                these are the mutating controls, not the read-only canvas itself. */}
            {editing && metricTreeViewPermission && (
              <>
                <HStack spacing={1}>
                  <Tooltip label="Undo (Ctrl+Z)">
                    <IconButton
                      size="sm" variant="ghost" aria-label="Undo" icon={<FiRotateCcw />}
                      onClick={undo} isDisabled={past.current.length === 0}
                    />
                  </Tooltip>
                  <Tooltip label="Redo (Ctrl+Shift+Z)">
                    <IconButton
                      size="sm" variant="ghost" aria-label="Redo" icon={<FiRotateCw />}
                      onClick={redo} isDisabled={future.current.length === 0}
                    />
                  </Tooltip>
                </HStack>
                <Button size="sm" leftIcon={<FiPlus />} variant="outline" onClick={() => handleAddNode('metric')}>
                  Add metric
                </Button>
                <Tooltip label="Auto-arrange the tree">
                  <Button size="sm" leftIcon={<FiGrid />} variant="outline" onClick={handleTidy}>
                    Tidy
                  </Button>
                </Tooltip>
              </>
            )}
            <Text fontSize="sm" color="gray.500" fontWeight="600">As of</Text>
            <Select
              size="sm" w="150px" value={timeFrame}
              onChange={(e) => setTimeFrame(e.target.value)}
            >
              {TIME_FRAMES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </Select>
            {metricTreeViewPermission && (
              <Button
                size="sm" leftIcon={<FiZap />} variant="outline" colorScheme="gray"
                onClick={() => handleCompute()} isLoading={computing} loadingText="Refreshing"
              >
                Refresh
              </Button>
            )}
            {editing ? (
              <>
                {metricTreeViewPermission && (
                  <Button
                    size="sm" leftIcon={<FiSave />} colorScheme="blue"
                    onClick={handleSave} isLoading={saving} loadingText="Saving"
                  >
                    Save
                  </Button>
                )}
                {!isNew && (
                  <Tooltip label="Leave edit mode">
                    <Button size="sm" leftIcon={<FiEye />} variant="ghost" onClick={exitToView}>
                      View
                    </Button>
                  </Tooltip>
                )}
              </>
            ) : (
              canEdit && (
                <Button size="sm" leftIcon={<FiEdit2 />} colorScheme="blue" onClick={enterEdit}>
                  Edit
                </Button>
              )
            )}
            {/* MT-01: owner-only actions (share + move scope). Only for a saved tree. */}
            {isOwner && !isNew && metricTreeSharePermission && (
              <Menu placement="bottom-end">
                <MenuButton
                  as={IconButton} size="sm" variant="ghost"
                  icon={<FiMoreVertical />} aria-label="Tree actions"
                />
                <MenuList>
                  {scope === 'personal' && (
                    <MenuItem icon={<FiShare2 />} onClick={openShare} isDisabled={scopeBusy}>
                      Share…
                    </MenuItem>
                  )}
                  {scope === 'personal' ? (
                    <MenuItem icon={<FiUsers />} onClick={() => moveScope('team')} isDisabled={scopeBusy}>
                      Move to Team
                    </MenuItem>
                  ) : (
                    <MenuItem icon={<FiUser />} onClick={() => moveScope('personal')} isDisabled={scopeBusy}>
                      Move to Personal
                    </MenuItem>
                  )}
                </MenuList>
              </Menu>
            )}
          </HStack>
        </Flex>

        {/* Canvas + panel */}
        <Flex flex={1} minH={0}>
          <Box flex={1} position="relative">
            {loading ? (
              <Flex align="center" justify="center" h="100%">
                <Spinner size="lg" color="blue.500" />
              </Flex>
            ) : (
              <ReactFlow
                nodes={displayNodes}
                edges={allEdges}
                nodeTypes={NODE_TYPES}
                edgeTypes={EDGE_TYPES}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onNodeDragStart={onNodeDragStart}
                onNodeDragStop={onNodeDragStop}
                onNodeClick={handleNodeClick}
                onPaneClick={handlePaneClick}
                // MT-02: view mode is fully read-only — no drag, no new
                // connections, no node deletion; selection stays on so the panel
                // can still be opened to inspect a node.
                nodesDraggable={editing && tool === 'select' && !layoutLocked}
                nodesConnectable={editing}
                edgesUpdatable={editing}
                deleteKeyCode={editing ? ['Backspace', 'Delete'] : null}
                panOnDrag
                fitView
                minZoom={0.2}
                proOptions={{ hideAttribution: true }}
              >
                <Background variant={BackgroundVariant.Dots} gap={22} size={1.1} color="#d3dbe6" />
                {/* MT-05: replace React Flow's stock "toggle interactivity" button
                    (which reads as a global read-only switch) with a product-labelled
                    lock that clearly affects LAYOUT only — editing metrics still works. */}
                <Controls showInteractive={false}>
                  <ControlButton
                    onClick={() => setLayoutLocked((v) => !v)}
                    title={layoutLocked
                      ? 'Layout locked — node positions are fixed. Click to unlock. (Editing metrics is still allowed.)'
                      : 'Lock layout — fix node positions. Editing metrics stays allowed.'}
                    aria-label={layoutLocked ? 'Unlock layout' : 'Lock layout'}
                  >
                    {layoutLocked ? <FiLock /> : <FiUnlock />}
                  </ControlButton>
                </Controls>
              </ReactFlow>
            )}

            {!loading && nodes.length === 0 && !showChooser && (
              <Box
                position="absolute" top="50%" left="50%"
                transform="translate(-50%, -50%)" textAlign="center" pointerEvents="none"
              >
                <Text fontSize="lg" fontWeight="700" color="gray.500">No metrics yet</Text>
                <Text fontSize="sm" color="gray.400">
                  {editing
                    ? 'Add a node from the palette below, or pick a template'
                    : (canEdit ? 'Click Edit to start building this tree' : 'This tree has no metrics yet')}
                </Text>
              </Box>
            )}

            {/* MT-02: the build palette + template chooser only exist in edit mode,
                and only for a user who can actually save — canEdit already covers
                metric-tree-create for a new tree's owner, not just metric-tree-view. */}
            {!loading && editing && canEdit && (
              <NodePalette tool={tool} onToolChange={setTool} onAdd={handleAddNode} />
            )}

            {/* "Not sure where to start?" template chooser (new trees, edit mode) */}
            {!loading && editing && canEdit && showChooser && (
              <TemplateChooser onPick={applyTemplate} onClose={() => setShowChooser(false)} />
            )}
          </Box>

          {/* Config panel is only mounted when a node is selected (Mixpanel behaviour). */}
          {selectedNode && (
            <MetricNodePanel
              selectedNode={selectedNode}
              onChange={patchSelectedNode}
              onDelete={deleteSelectedNode}
              onClose={() => setSelectedNodeId(null)}
              readOnly={!editing}
              events={events}
              eventsError={eventsError}
              eventsLoading={eventsLoading}
              allNodes={nodes.map((n) => ({
              id: n.id,
              label: n.data?.label || n.id,
              type: n.data?.type || 'metric',
              kind: n.data?.kind === 'formula' ? 'formula' : 'metric',
              inputs: Array.isArray(n.data?.formula?.inputs) ? n.data.formula.inputs : [],
            }))}
            />
          )}
        </Flex>
      </Flex>

      {/* MT-01: Share dialog — owner picks tenant users who can view+edit a
          PERSONAL tree (team trees are already visible team-wide). */}
      <Modal isOpen={shareModal.isOpen} onClose={shareModal.onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">Share metric tree</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <FormLabel fontSize="sm">People with access</FormLabel>

              <HStack spacing={2} wrap="wrap" mb={3}>
                <HStack spacing={1} bg="gray.100" borderRadius="full" px={2} py={1}>
                  <FiUser size={12} />
                  <Text fontSize="xs" fontWeight={600}>
                    {(userDir.find((u) => u.id === String(ownerId))?.name) || 'Owner'} · owner
                  </Text>
                </HStack>
                {shareSelected.map((s) => (
                  <HStack key={s.id} spacing={1} bg="#e0e7ff" color="#3730a3" borderRadius="full" pl={2} pr={1} py={1}>
                    <Text fontSize="xs" fontWeight={600}>{s.name}</Text>
                    <IconButton
                      aria-label={`Remove ${s.name}`} icon={<FiX />} size="2xs" variant="ghost"
                      color="#3730a3" minW={4} h={4} onClick={() => removeShareUser(s.id)}
                    />
                  </HStack>
                ))}
              </HStack>

              <Input
                autoFocus
                value={userQuery}
                onChange={(e) => setUserQuery(e.target.value)}
                placeholder="Search people by name or email…"
              />
              {userQuery.trim() && (
                <Box mt={1} maxH="220px" overflowY="auto" borderWidth="1px" borderColor="gray.200" borderRadius="md">
                  {userDirState === 'loading' && (
                    <HStack p={3} color="gray.500"><Spinner size="xs" /><Text fontSize="sm">Loading people…</Text></HStack>
                  )}
                  {userDirState === 'error' && (
                    <Text p={3} fontSize="sm" color="orange.500">
                      Couldn&apos;t load the user directory (need the user-list permission).
                    </Text>
                  )}
                  {userDirState === 'ready' && shareCandidates.length === 0 && (
                    <Text p={3} fontSize="sm" color="gray.500">No users found matching “{userQuery.trim()}”.</Text>
                  )}
                  {userDirState === 'ready' && shareCandidates.map((u) => (
                    <HStack
                      key={u.id} as="button" type="button" w="100%" justify="space-between"
                      px={3} py={2} _hover={{ bg: 'gray.50' }} onClick={() => addShareUser(u)} textAlign="left"
                    >
                      <Box textAlign="left">
                        <Text fontSize="sm" fontWeight={600}>{u.name}</Text>
                        {u.email ? <Text fontSize="xs" color="gray.500">{u.email}</Text> : null}
                      </Box>
                      <FiPlus />
                    </HStack>
                  ))}
                </Box>
              )}
              <Text fontSize="xs" color="gray.500" mt={2}>
                Shared users can view and edit this tree. No limit on how many you add.
              </Text>
            </FormControl>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button size="sm" variant="ghost" onClick={shareModal.onClose}>Cancel</Button>
            <Button size="sm" colorScheme="blue" onClick={saveShare} isLoading={scopeBusy}>
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

// ── Mappers between the backend tree shape and React Flow node/edge shape ──
function toRfNode(n) {
  const type = n.type || 'metric';
  return {
    id: n.node_id,
    type: rfTypeFor(type),
    position: n.position || { x: 0, y: 0 },
    data: {
      label: n.label || 'Metric',
      owner: n.owner || '',
      pinned: !!n.pinned,
      comments: Array.isArray(n.comments) ? n.comments : [],
      type,
      kind: n.kind === 'formula' ? 'formula' : 'metric',
      metric: n.metric || { event: '', agg: 'count' },
      formula: {
        operator: n.formula?.operator || '+',
        inputs: Array.isArray(n.formula?.inputs) ? n.formula.inputs : [],
      },
      annotation: n.annotation || {},
    },
  };
}
function fromRfNode(n) {
  const type = n.data?.type || 'metric';
  const base = {
    node_id: n.id,
    label: n.data?.label || 'Metric',
    owner: n.data?.owner || '',
    pinned: !!n.data?.pinned,
    comments: Array.isArray(n.data?.comments) ? n.data.comments : [],
    type,
    position: n.position || { x: 0, y: 0 },
  };
  // Annotation nodes (strategy/note/text/media) carry no metric/formula.
  if (type !== 'metric') {
    return { ...base, annotation: n.data?.annotation || {} };
  }
  const kind = n.data?.kind === 'formula' ? 'formula' : 'metric';
  base.kind = kind;
  if (kind === 'formula') {
    return {
      ...base,
      formula: {
        operator: n.data?.formula?.operator || '+',
        inputs: n.data?.formula?.inputs || [],
      },
    };
  }
  return { ...base, metric: n.data?.metric || { event: '', agg: 'count' } };
}
function toRfEdge(e) {
  return {
    id: e.edge_id,
    source: e.source,
    target: e.target,
    ...(e.source_handle ? { sourceHandle: e.source_handle } : {}),
    ...(e.target_handle ? { targetHandle: e.target_handle } : {}),
    type: 'default',
    markerEnd: { type: MarkerType.ArrowClosed, color: '#C2CCD9' },
    // Dashed grey connector (scaffold/hierarchy look); recoloured solid once a
    // correlation is computed (see handleCompute).
    style: { strokeWidth: 1.5, stroke: '#C2CCD9', strokeDasharray: '6 5' },
    data: { kind: e.kind || 'correlation', formula: e.formula, corr: e.corr },
  };
}
function fromRfEdge(e) {
  return {
    edge_id: e.id,
    source: e.source,
    target: e.target,
    ...(e.sourceHandle ? { source_handle: e.sourceHandle } : {}),
    ...(e.targetHandle ? { target_handle: e.targetHandle } : {}),
    kind: e.data?.kind || 'correlation',
    ...(e.data?.formula ? { formula: e.data.formula } : {}),
    ...(e.data?.corr ? { corr: e.data.corr } : {}),
  };
}

export default function MetricTreeCanvas() {
  return (
    <ReactFlowProvider>
      <CanvasInner />
    </ReactFlowProvider>
  );
}
